import React from "react";
import { Box, Button, TextField, Typography, CircularProgress } from "@mui/material";
import { purple } from "@/theme/colors";
import { useNewsletterSignup } from "@/hooks/useNewsletterSignup";

// ---------------------------------------------------------------------------
// simpleForm — importable newsletter signup form.
//   Renders an email input + subscribe button, or a confirmation box when the
//   user has already signed up.  Use the `source` prop to tag where the signup
//   originated (e.g. "footer", "navbar", "subscription-banner").
//   Optionally pass a `description` string; it is shown above the form and
//   hidden once the user confirms.
// ---------------------------------------------------------------------------
import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
export function simpleForm({
  source = "site",
  description,
  column = false,
  placeholder = "Your email…",
  showUnsubscribeNote = false
} = {}) {
  const {
    email,
    setEmail,
    honeypot,
    setHoneypot,
    confirmed,
    submitting,
    submit
  } = useNewsletterSignup(source);
  if (confirmed) {
    return /*#__PURE__*/_jsxs(Box, {
      sx: {
        py: 1.5,
        px: 2,
        borderRadius: 1,
        border: `1px solid ${purple.primary}`,
        backgroundColor: "rgba(156, 39, 176, 0.08)",
        textAlign: "center"
      },
      children: [/*#__PURE__*/_jsxs(Typography, {
        variant: "subtitle2",
        sx: {
          fontWeight: 600,
          color: purple.dark
        },
        children: ["Confirmed signup for ", email || "your email"]
      }), /*#__PURE__*/_jsx(Typography, {
        variant: "body2",
        sx: {
          mt: 0.5
        },
        children: "Your signup has been received. When we send our next newsletter you will be the first to know!"
      })]
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [description && /*#__PURE__*/_jsx(Typography, {
      variant: "body2",
      sx: {
        mb: 1.5,
        color: "text.secondary"
      },
      children: description
    }), /*#__PURE__*/_jsxs(Box, {
      component: "form",
      onSubmit: e => {
        e.preventDefault();
        submit();
      },
      sx: {
        display: "flex",
        flexDirection: column ? "column" : "row",
        alignItems: column ? "stretch" : "center",
        gap: 1,
        flexWrap: column ? "nowrap" : "wrap",
        mb: 0
      },
      suppressHydrationWarning: true,
      children: [/*#__PURE__*/_jsx("input", {
        type: "text",
        name: "website",
        value: honeypot,
        onChange: e => setHoneypot(e.target.value),
        autoComplete: "off",
        tabIndex: -1,
        style: {
          position: "absolute",
          left: "-9999px"
        },
        "aria-hidden": "true"
      }), /*#__PURE__*/_jsx(TextField, {
        size: "small",
        type: "email",
        placeholder: placeholder,
        value: email,
        onChange: e => setEmail(e.target.value),
        suppressHydrationWarning: true,
        fullWidth: column,
        sx: column ? undefined : {
          flex: "1 1 160px"
        }
      }), /*#__PURE__*/_jsx(Button, {
        variant: "contained",
        color: "primary",
        type: "submit",
        disabled: submitting || !email,
        fullWidth: column,
        sx: {
          flexShrink: 0
        },
        children: submitting ? /*#__PURE__*/_jsx(CircularProgress, {
          size: 16,
          sx: {
            color: "inherit"
          }
        }) : "Subscribe"
      }), showUnsubscribeNote && /*#__PURE__*/_jsx(Typography, {
        variant: "caption",
        sx: {
          color: "text.secondary",
          textAlign: "center",
          mt: 0.5
        },
        children: "You may unsubscribe at any time."
      })]
    })]
  });
}
export default simpleForm;